import { Loader2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { OnboardingShell } from '@/components/Onboarding/OnboardingShell';
import { OnboardingStepProfile } from '@/components/Onboarding/OnboardingStepProfile';
import { OnboardingStepTerms } from '@/components/Onboarding/OnboardingStepTerms';
import { getPartner } from '@/data/partnersRepo';
import { useSubmitPartnerOnboarding } from '@/hooks/useSubmitPartnerOnboarding';
import {
  makePartnerProfileDraft,
  missingPartnerProfileFields,
  partnerOnboardingComplete,
  type PartnerProfileDraftType,
  type PartnerType,
} from '@/types/types_partner';
import { useAuth } from './AuthContext';

/** The outcome of loading one partner record, tagged with which one it was. */
interface LoadResultType {
  partnerId: string;
  /** Null both when the document is missing and when the read failed. */
  partner: PartnerType | null;
  failed?: boolean;
}

/**
 * Holds a partner manager at the registration wizard until their partner record is
 * complete and the terms are accepted.
 *
 * Wrapped around the whole app rather than the /partner route (see App.tsx),
 * because the agreement governs what a partner may do with the experiences —
 * their names, their locations, their prices — and all of that is on the
 * catalogue. Gating only the profile page would let someone browse the very
 * thing the NDA covers while declining to sign it.
 *
 * Three cases pass straight through:
 *
 *   - Eighty Days admins. An admin who also manages a partner must not be locked
 *     out of the CMS by that partner's paperwork.
 *   - Staff accounts. They cannot write the partner record at all, so asking
 *     them would be a dead end; their manager is the one being chased.
 *   - Managers whose partner document is missing. The profile page explains that
 *     case properly, and a wizard writing into nothing would not.
 *
 * New managers normally never see this: the invitation wizard collects the same
 * two steps before the account exists. This is what catches everyone else —
 * accounts that predate the requirement, and sign-ups where the final write
 * failed after the account had been created.
 */
export function PartnerOnboardingGate({ children }: { children: ReactNode }) {
  const { user, isAdmin, partnerUser, signOutUser } = useAuth();

  const needsOnboarding = !isAdmin && partnerUser?.role === 'owner' && partnerUser.status === 'active';
  const partnerId = needsOnboarding ? partnerUser.partnerId : undefined;

  // One piece of state, stamped with the partner it describes, rather than
  // separate `partner` / `loading` / `failed` flags: "still loading" is then a
  // derived fact, and the effect never has to reset anything synchronously to
  // stop a stale record from being read as this one.
  const [result, setResult] = useState<LoadResultType | null>(null);
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<PartnerProfileDraftType>(() => makePartnerProfileDraft());
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitOnboarding = useSubmitPartnerOnboarding(partnerId);

  useEffect(() => {
    if (!partnerId) return;
    let cancelled = false;
    getPartner(partnerId)
      .then((loaded) => {
        if (cancelled) return;
        setResult({ partnerId, partner: loaded ?? null });
        // Seeded with whatever Eighty Days already filled in, so a manager
        // confirms and corrects rather than retyping the whole record.
        setDraft(makePartnerProfileDraft(loaded));
        setAccepted(!!loaded?.termsAcceptedAt);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Loading partner for onboarding failed', e);
        setResult({ partnerId, partner: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  // Anything stamped with a different partner belongs to the previous session.
  const loaded = result?.partnerId === partnerId ? result : undefined;
  const partner = loaded?.partner ?? null;

  async function handleComplete() {
    if (!partner || !user || !partnerId) return;
    setBusy(true);
    setError(null);
    try {
      await submitOnboarding(draft, user.uid);
      // Reflecting the write locally is what dismisses the gate — the next
      // render sees a complete record and hands over to the app.
      setResult({
        partnerId,
        partner: { ...partner, ...draft, termsAcceptedAt: new Date().toISOString(), termsAcceptedByUid: user.uid },
      });
    } catch (e) {
      console.error('Saving partner onboarding failed', e);
      setError('Could not save your details. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (!partnerId) return <>{children}</>;

  if (!loaded) {
    return (
      <div className='tw:flex tw:min-h-screen tw:items-center tw:justify-center tw:bg-ed-ink'>
        <Loader2 className='tw:size-8 tw:animate-spin tw:text-ed-gold' />
      </div>
    );
  }

  // Not waved through: whether registration is outstanding is unknown, and
  // guessing "complete" would be the one wrong guess. Says so plainly instead.
  if (loaded.failed) {
    return (
      <div className='tw:flex tw:min-h-screen tw:flex-col tw:items-center tw:justify-center tw:bg-ed-ink tw:px-6 tw:text-center'>
        <h1 className='tw:font-serif tw:text-3xl tw:text-white'>Something went wrong</h1>
        <p className='tw:mt-3 tw:max-w-md tw:text-sm tw:text-white/60'>
          We couldn&rsquo;t load your partner&rsquo;s details. Please reload the page, or contact Eighty Days if this
          keeps happening.
        </p>
        <button
          type='button'
          onClick={() => window.location.reload()}
          className='ed-btn-gold tw:mt-8 tw:rounded-sm tw:px-10 tw:py-3 tw:text-sm tw:tracking-[0.15em] tw:text-ed-black tw:uppercase'
        >
          Reload
        </button>
      </div>
    );
  }

  if (!partner || partnerOnboardingComplete(partner)) return <>{children}</>;

  return (
    <OnboardingShell
      step={step}
      totalSteps={2}
      title='Complete Your Registration'
      subtitle={`A few details about ${partner.name} before you begin`}
      action={
        <button
          type='button'
          onClick={() => void signOutUser()}
          className='tw:text-sm tw:text-white/60 tw:transition-colors tw:hover:text-ed-gold'
        >
          Sign out
        </button>
      }
    >
      {step === 1 ? (
        <OnboardingStepProfile
          draft={draft}
          onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
          partnerName={partner.name}
          brand={partner.brand}
          continueLabel='Continue to Terms'
          onContinue={(next) => {
            if (missingPartnerProfileFields(next).length) return;
            setStep(2);
          }}
        />
      ) : (
        <OnboardingStepTerms
          accepted={accepted}
          onAcceptedChange={setAccepted}
          onBack={() => setStep(1)}
          onContinue={() => void handleComplete()}
          continueLabel='Complete Registration'
          busy={busy}
          error={error}
        />
      )}
    </OnboardingShell>
  );
}
